import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.array.filter.js';
import 'core-js/modules/es.array.find.js';
import 'core-js/modules/es.array.from.js';
import 'core-js/modules/es.array.includes.js';
import 'core-js/modules/es.array.map.js';
import 'core-js/modules/es.object.assign.js';
import 'core-js/modules/es.object.to-string.js';
import 'core-js/modules/es.string.includes.js';
import 'core-js/modules/es.string.iterator.js';
import 'core-js/modules/web.timers.js';
import { __rest } from 'tslib';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React, { useState, useEffect, useRef } from 'react';
import { Tabs as Tabs$1, TabsList, TabPanel, Tab } from '@mui/base';
import classNames from 'classnames';
import { ChevronDownIcon } from '@cognite/cogs-icons';
import { getThemeClassNames, IconWrapper } from '@cognite/cogs-utils';
import { Button } from '../button/button.js';
import { CounterChip } from '../counter-chip/counter-chip.js';
import { Dropdown } from '../dropdown/dropdown.js';
import { Menu } from '../menu/menu.js';
import { Skeleton } from '../skeleton/skeleton.js';

/**
 * Tabs
 * =============================================================================
 * Tabs enable you to relate multiple navigation endpoints through a single
 * object structure. This helps establish a sense of relativity between views.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-tabs--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Tabs>
 *   <Tabs.Tab label="Histogram" key="1">
 *     Histogram view
 *   </Tabs.Tab>
 *   <Tabs.Tab label="Graph" key="2">
 *     Graph view
 *   </Tabs.Tab>
 *   <Tabs.Tab label="List" key="3">
 *     List view
 *   </Tabs.Tab>
 * </Tabs>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param activeKey           - The key of the tab that is currently active.
 * @param children            - Add `children` to the tab components to automatically make it show/hide when the tab is active. This is the easiest implementation of the Tabs component, without the need to write custom logic.
 * @param defaultActiveKey    - Initial active tabPanel's key if activeKey is absent.
 * @param fullWidth           - Setting the `fullWidth` prop to `true` will stretch the tabs to fill the width of the container.
 * @param hidePadding         - Setting the `hidePadding` prop to `true` will remove the padding from around the tabs.
 * @param onChange            - This function runs when the active tab is changed.
 * @param onTabClick          - This function runs when a tab is clicked.
 * @param showTrack           - Setting the `showTrack` prop to `true` will show a bottom border across all tabs.
 * @param size                - The size of the tabs.
 * @param skeleton            - The skeleton version of the tabs component.
 * @param tabPlacement         - Where to show the tabs in relation to the content.
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-tabs--base)
 */
var Tabs = function Tabs(_ref) {
  var propsActiveKey = _ref.activeKey,
    children = _ref.children,
    className = _ref.className,
    defaultActiveKey = _ref.defaultActiveKey,
    fullWidth = _ref.fullWidth,
    hidePadding = _ref.hidePadding,
    showTrack = _ref.showTrack,
    _ref$size = _ref.size,
    size = _ref$size === undefined ? 'large' : _ref$size,
    _ref$iconOnly = _ref.iconOnly,
    iconOnly = _ref$iconOnly === undefined ? false : _ref$iconOnly,
    skeleton = _ref.skeleton,
    _ref$tabPlacement = _ref.tabPlacement,
    tabPlacement = _ref$tabPlacement === undefined ? 'top' : _ref$tabPlacement,
    onChange = _ref.onChange,
    onTabClick = _ref.onTabClick,
    themeProps = _ref.themeProps;
  var _a;
  var tabs = children.map(function (tab) {
    return tab.props.tabKey;
  });
  var _useState = useState([]),
    overflownTabs = _useState[0],
    setOverflownTabs = _useState[1];
  var initialActiveDefault = defaultActiveKey || ((_a = children.find(function (item) {
    return !item.props.disabled;
  })) === null || _a === undefined ? undefined : _a.props.tabKey);
  var _useState2 = useState(propsActiveKey !== null && propsActiveKey !== undefined ? propsActiveKey : initialActiveDefault),
    currentActiveKey = _useState2[0],
    setCurrentActiveKey = _useState2[1];
  /**
   * priority is to the active key passed by user (controlled state)
   */
  useEffect(function () {
    if (propsActiveKey) setCurrentActiveKey(propsActiveKey);
  }, [propsActiveKey]);
  var _useState3 = useState(false),
    isOverflown = _useState3[0],
    setIsOverflown = _useState3[1];
  var tabContainerRef = useRef(null);
  var tabRef = useRef(null);
  var scrollRef = useRef(null);
  var checkOverflowTimeoutRef = useRef();
  var checkOverflow = function checkOverflow() {
    if (!tabContainerRef.current || !scrollRef.current) return;
    var containerRect = tabContainerRef.current.getBoundingClientRect();
    var overflownTabsElements = Array.from(scrollRef.current.children).filter(function (el) {
      var childRect = el.getBoundingClientRect();
      return Math.floor(childRect.right) > Math.floor(containerRect.right) || Math.floor(childRect.left) < Math.floor(containerRect.left);
    });
    setOverflownTabs(overflownTabsElements.map(function (el) {
      var _a;
      return (_a = el.getAttribute('data-tabkey')) !== null && _a !== undefined ? _a : '';
    }));
    setIsOverflown(overflownTabsElements.length > 0);
  };
  useEffect(function () {
    checkOverflow();
    function checkOverflowTimeout() {
      clearTimeout(checkOverflowTimeoutRef.current);
      // we need the timeout to make sure the DOM is updated before we check for overflow
      checkOverflowTimeoutRef.current = setTimeout(function () {
        checkOverflow();
      }, 0);
    }
    var scrollRefTarget = scrollRef.current;
    // this is needed to identify whether the tab list is overflowing the container on resize as well.
    window.addEventListener('resize', checkOverflowTimeout);
    scrollRefTarget === null || scrollRefTarget === undefined ? undefined : scrollRefTarget.addEventListener('scroll', checkOverflowTimeout);
    return function () {
      window.removeEventListener('resize', checkOverflowTimeout);
      scrollRefTarget === null || scrollRefTarget === undefined ? undefined : scrollRefTarget.removeEventListener('scroll', checkOverflowTimeout);
    };
  }, []);
  /**
   * this effect is unclear. But not removing since it's been there for sometime.
   * will have to evaluate it's validity during refactor.
   */
  useEffect(function () {
    if ((scrollRef === null || scrollRef === undefined ? undefined : scrollRef.current) && propsActiveKey && isOverflown) setCurrentActiveKey(propsActiveKey);
  }, [isOverflown, propsActiveKey]);
  var onTabChanged = function onTabChanged(event, value) {
    if (onChange) onChange(event, value);
    if (onTabClick) onTabClick("" + value, event);
    setCurrentActiveKey("" + value);
  };
  if (skeleton) {
    return jsx(Skeleton.Rectangle, {
      height: "46px",
      width: "100%"
    });
  }
  return jsxs(Tabs$1, {
    value: currentActiveKey,
    onChange: onTabChanged,
    className: classNames.apply(undefined, ['cogs-v10', 'cogs-tabs', "cogs-tabs--placement-" + tabPlacement].concat(getThemeClassNames(undefined, themeProps))),
    ref: tabContainerRef,
    children: [jsxs("div", {
      className: classNames('cogs-v10', 'cogs-tabs__tabbar', showTrack && 'cogs-tabs__tabbar--show-track'),
      children: [jsx(TabsList, {
        className: classNames('cogs-v10', 'cogs-tabs__list', "cogs-tabs--size-" + size, fullWidth && 'cogs-tabs--full-width', className),
        ref: scrollRef,
        role: "tablist",
        children: tabs.map(function (tab, i) {
          var childProps = children[i].props;
          return /*#__PURE__*/React.cloneElement(children[i], Object.assign(Object.assign({}, childProps), {
            key: tab,
            tabRef: tab === currentActiveKey ? tabRef : null,
            role: 'tab',
            iconOnly: iconOnly,
            children: children[i].props.children,
            className: classNames(hidePadding && "cogs-tabs--hide-padding"),
            'aria-selected': tab === currentActiveKey,
            'data-tabkey': tab,
            tabKey: tab,
            'aria-disabled': childProps.disabled,
            onClick: function onClick(evt) {
              var isButtonDisabled = childProps.disabled;
              if (!isButtonDisabled) {
                setCurrentActiveKey("" + tab);
              }
              if (onTabClick) onTabClick("" + tab, evt);
              if (childProps.onClick) childProps.onClick(evt);
            }
          }));
        })
      }), isOverflown && jsx(Dropdown, {
        hideOnSelect: true,
        hideOnClick: true,
        content: jsx(Menu, {
          children: tabs.map(function (tab, i) {
            return overflownTabs.includes(tab) ? jsx(Menu.Item, {
              onClick: function onClick(event) {
                setCurrentActiveKey("" + tab);
                if (onTabClick) onTabClick(tab, event);
              },
              toggled: currentActiveKey === "" + tab,
              // icon={
              //   children[i].props.iconLeft ||
              //   children[i].props.iconRight
              // }
              iconPlacement: children[i].props.iconLeft ? 'left' : 'right',
              children: children[i].props.label
            }, tab) : null;
          })
        }),
        children: jsx(Button, {
          className: "cogs-v10 cogs-tabs__dropdown",
          "aria-label": "Open tab list",
          icon: jsx(ChevronDownIcon, {}),
          type: "ghost",
          "data-testid": "cogs-tabs__dropdown"
        })
      })]
    }), tabs.map(function (tab, i) {
      return jsx(TabPanel, {
        className: "cogs-v10 cogs-tabs__children",
        value: tab,
        children: children[i].props.children
      }, tab);
    })]
  });
};
/**
 * Tabs.Tab
 * =============================================================================
 * Subcomponent of Tabs. Tabs enable you to relate multiple navigation endpoints
 * through a single object structure. This helps establish a sense of relativity
 * between views.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-tabs--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Tabs>
 *   <Tabs.Tab label="Histogram" tabKey="1">
 *     Histogram view
 *   </Tabs.Tab>
 *   <Tabs.Tab label="Graph" tabKey="2">
 *     Graph view
 *   </Tabs.Tab>
 *   <Tabs.Tab label="List" tabKey="3">
 *     List view
 *   </Tabs.Tab>
 * </Tabs>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param chipLeft            - Add a chip to the tab on the left of the label.
 * @param chipRight           - Add a chip to the tab on the right of the label.
 * @param disabled            - The tab becomes unclickable by mouse and unpressable by keyboard events.
 * @param iconLeft            - Add an icon to the tab on the left of the label.
 * @param iconRight           - Add an icon to the tab on the right of the label.
 * @param label               - Content of the tab, this can only be a string.
 * @param tabRef              - A reference to the tab.
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-tabs--base)
 */
Tabs.Tab = function (_a) {
  var className = _a.className,
    disabled = _a.disabled,
    dropdown = _a.dropdown,
    iconRight = _a.iconRight,
    iconLeft = _a.iconLeft,
    tabKey = _a.tabKey,
    label = _a.label,
    tabRef = _a.tabRef,
    chip = _a.chip,
    iconOnly = _a.iconOnly,
    onClick = _a.onClick,
    rest = __rest(_a, ["className", "disabled", "dropdown", "iconRight", "iconLeft", "tabKey", "label", "tabRef", "chip", "iconOnly", "onClick"]);
  var renderTabContent = function renderTabContent() {
    return jsxs(Fragment, {
      children: [iconLeft && jsx(IconWrapper, {
        className: "cogs-v10 cogs-tabs__icon-wrapper--left",
        size: 16,
        children: iconLeft
      }), label, iconRight && !dropdown && jsx(IconWrapper, {
        className: "cogs-v10 cogs-tabs__icon-wrapper--right",
        size: 16,
        children: iconRight
      }), dropdown && jsx(ChevronDownIcon, {})]
    });
  };
  var renderTab = function renderTab() {
    return jsx(Tab, Object.assign({
      ref: tabRef,
      role: "tab",
      className: classNames('cogs-v10', 'cogs-tabs__list__tab', disabled && 'cogs-tabs__list__tab--disabled', className),
      disabled: disabled,
      "aria-hidden": disabled,
      value: tabKey
    }, rest, {
      onMouseDown: onClick,
      children: renderTabContent()
    }), tabKey);
  };
  return dropdown ? jsx(Dropdown, Object.assign({
    offset: [2, 2],
    disabled: disabled
  }, dropdown, {
    children: renderTab()
  })) : jsxs(Tab, Object.assign({
    ref: tabRef
  }, iconOnly && typeof label === 'string' ? {
    'aria-label': label
  } : {}, {
    className: classNames('cogs-v10', 'cogs-tabs__list__tab', disabled && 'cogs-tabs__list__tab--disabled', className),
    disabled: disabled,
    "aria-hidden": disabled,
    value: tabKey
  }, rest, {
    children: [iconLeft && jsx(IconWrapper, {
      className: "cogs-v10 cogs-tabs__icon-wrapper--left",
      size: 16,
      children: iconLeft
    }), !iconOnly && label, !iconOnly && iconRight && !dropdown && jsx(IconWrapper, {
      className: "cogs-v10 cogs-tabs__icon-wrapper--right",
      size: 16,
      children: iconRight
    }), chip && jsx(CounterChip, Object.assign({}, chip, {
      className: "chip-right"
    }))]
  }), tabKey);
};

export { Tabs };
